'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Thin top progress bar (NProgress-style) + click-capture overlay.
 * Listens to window events emitted by api.ts:
 *   - 'armorik:loading:start'  — non-silent apiFetch started
 *   - 'armorik:loading:done'   — non-silent apiFetch finished (success or error)
 *
 * The overlay shows whenever at least one non-silent API request is in flight.
 * Background requests (SWR revalidations, periodic polls) should use `silent`
 * in apiFetch to avoid showing the overlay.
 *
 * Multiple concurrent requests are counted so the overlay stays until all finish.
 * apiFetch always calls notifyLoadingDone in a finally block, so the count
 * can never get stuck.
 */

type Phase = 'idle' | 'running' | 'finishing';

const START_EVENT = 'armorik:loading:start';
const DONE_EVENT = 'armorik:loading:done';
const RESET_EVENT = 'armorik:loading:reset';

export function notifyLoadingStart() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(START_EVENT));
  }
}

export function notifyLoadingDone() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(DONE_EVENT));
  }
}

export function notifyLoadingReset() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(RESET_EVENT));
  }
}

export default function LoadingBar({ scoped = false }: { scoped?: boolean } = {}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const activeRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const finishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleRef = useRef(false);

  useEffect(() => {
    const tick = () => {
      rafRef.current = null;
      setProgress((p) => {
        if (p >= 0.8) return p;
        const next = p + (0.8 - p) * 0.1;
        return next > 0.79 ? 0.8 : next;
      });
      if (activeRef.current > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    const startTick = () => {
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    const stopTick = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    // Safety watchdog: if requests stay in flight for too long (e.g. a
    // component unmounts but its SWR fetch is still running on a slow
    // endpoint), force-reset the overlay so the UI doesn't get stuck.
    const MAX_OVERLAY_MS = 10_000;

    const resetWatchdog = () => {
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      watchdogRef.current = setTimeout(() => {
        watchdogRef.current = null;
        if (activeRef.current > 0) {
          activeRef.current = 0;
          if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
          }
          stopTick();
          if (visibleRef.current) {
            visibleRef.current = false;
            setProgress(1);
            setPhase('finishing');
            finishTimerRef.current = setTimeout(() => {
              setPhase('idle');
              setProgress(0);
              finishTimerRef.current = null;
            }, 400);
          } else {
            setPhase('idle');
            setProgress(0);
          }
        }
      }, MAX_OVERLAY_MS);
    };

    // Show delay: fast requests (<300 ms) never render the bar, so short
    // local API calls don't produce a visible flash.
    const SHOW_DELAY_MS = 300;

    const handleStart = () => {
      activeRef.current += 1;
      if (finishTimerRef.current) {
        clearTimeout(finishTimerRef.current);
        finishTimerRef.current = null;
      }
      if (showTimerRef.current == null && !visibleRef.current) {
        showTimerRef.current = setTimeout(() => {
          showTimerRef.current = null;
          if (activeRef.current > 0) {
            visibleRef.current = true;
            setPhase('running');
            setProgress((p) => (p > 0 ? p : 0));
            startTick();
          }
        }, SHOW_DELAY_MS);
      }
      resetWatchdog();
    };

    const handleDone = () => {
      if (activeRef.current === 0) return; // ignore stray done events
      activeRef.current -= 1;
      if (activeRef.current > 0) return; // still other requests in flight
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      if (!visibleRef.current) return; // request finished before the bar appeared
      visibleRef.current = false;
      stopTick();
      setProgress(1);
      setPhase('finishing');
      finishTimerRef.current = setTimeout(() => {
        setPhase('idle');
        setProgress(0);
        finishTimerRef.current = null;
      }, 400);
    };

    const handleReset = () => {
      activeRef.current = 0;
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      visibleRef.current = false;
      stopTick();
      setProgress(0);
      setPhase('idle');
    };

    window.addEventListener(START_EVENT, handleStart);
    window.addEventListener(DONE_EVENT, handleDone);
    window.addEventListener(RESET_EVENT, handleReset);

    return () => {
      window.removeEventListener(START_EVENT, handleStart);
      window.removeEventListener(DONE_EVENT, handleDone);
      window.removeEventListener(RESET_EVENT, handleReset);
      stopTick();
      if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
    };
  }, []);

  if (phase === 'idle') return null;

  const widthPct = Math.round(progress * 100);
  return (
    <>
      {/* Click-capture overlay: blocks interaction while API requests are
          in flight. Only shown during 'running' phase — NOT during
          'finishing', so the user can click rows as soon as data arrives. */}
      {phase === 'running' && scoped && (
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center"
          style={{
            zIndex: 9998,
            cursor: 'wait',
            background: 'rgba(0, 0, 0, 0.08)',
          }}
        >
          <div className="w-6 h-6 border-2 border-wood/20 border-t-wood rounded-full animate-spin" />
        </div>
      )}
      {phase === 'running' && !scoped && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            cursor: 'wait',
            background: 'rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(0.5px)',
          }}
        />
      )}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          zIndex: 9999,
          pointerEvents: 'none',
          background: 'transparent',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${widthPct}%`,
            transition:
              phase === 'finishing'
                ? 'width 200ms ease-out, opacity 200ms ease-out'
                : 'width 300ms ease-out',
            opacity: phase === 'finishing' ? 0 : 1,
            background: 'linear-gradient(90deg, var(--color-moss), var(--color-ochre))',
            boxShadow: '0 0 8px rgba(92, 122, 82, 0.6)',
          }}
        />
      </div>
    </>
  );
}
